<script setup lang="ts">
    import { ISortDirection, ResponseColumnHeader } from '@/js/services/api/interaction/types';
    import { __, __untypedExpression, hasExpression } from '@/js/stores/expressions';
    import { ref, computed } from 'vue';
    import ColumnFilter from './ColumnFilter.vue';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import Modal, { ModalSize } from '../Modal.vue';
    import IconButton from '../IconButton.vue';
    import {
        ArrowDownIcon,
        ArrowUpIcon,
        ChartColumnIcon,
        CirclePlusIcon,
        CircleMinusIcon,
        InfoIcon,
        MoveIcon,
        XIcon,
    } from '@lucide/vue';

    const header = defineModel<ResponseColumnHeader>('header', { required: true });

    const infoText = computed(() => {
        const key = `${header.value.translationKey}-info`;
        return hasExpression(key) ? __untypedExpression(key) : '';
    });

    const props = defineProps<{
        graph?: boolean;
        sortFieldId?: string;
        sortOrder?: ISortDirection;
        filterStoreName: FilterStoreName;
        expandable?: boolean;
        expanded?: boolean;
        disableLayoutShift?: boolean;
        color?: string;
    }>();

    const emit = defineEmits<{
        (e: 'sort', fieldId: string, sortOrder: 'asc' | 'desc'): void;
        (e: 'configColumn', column: ResponseColumnHeader): void;
        (e: 'toggleExpansion', columnId: string): void;
        (e: 'hideColumn', column: ResponseColumnHeader): void;
    }>();

    const onHideColumn = () => {
        header.value.order = null;
        emit('hideColumn', header.value);
    };

    const infoModal = ref(false);

    const closeInfoModal = () => {
        infoModal.value = false;
    };

    const isDateTimeColumn = () =>
        header.value.translationKey === 'table-interaction-column-date-time';

    const isActiveSort = (direction: 'asc' | 'desc') => {
        const hasExplicitSort = !!props.sortFieldId && !!props.sortOrder;

        if (hasExplicitSort) {
            const sortDirection = props.sortOrder!.toLowerCase() as 'asc' | 'desc';
            return props.sortFieldId === header.value.id && sortDirection === direction;
        }

        // Backend default for interactions table on first load
        return isDateTimeColumn() && direction === 'desc';
    };
</script>

<template>
    <Modal :show="infoModal" :size="ModalSize.Large" @close="closeInfoModal">
        <h2 class="heading-md mb-1">{{ __untypedExpression(header.translationKey) }}</h2>
        <p>{{ infoText }}</p>
    </Modal>
    <div
        class="group p-3 min-h-22 w-full min-w-0 overflow-hidden flex flex-col justify-between relative"
        style="min-width: 10rem"
    >
        <div v-if="color" class="absolute top-0 left-0 right-0 h-1.5" :class="color"></div>
        <ColumnFilter
            :filter-key="header.filterKey"
            :translation-key="header.translationKey"
            :dynamic-filter-key-name="header.dynamicFilterKeyName"
            :filter-store-name="filterStoreName"
        />
        <div class="flex items-center h-6 self-start shrink-0">
            <IconButton v-if="graph" area-size="size-5" size="size-4" aria-label="chart">
                <ChartColumnIcon class="size-4" />
            </IconButton>

            <IconButton
                v-if="header.sortable"
                key="asc"
                area-size="size-5"
                size="size-3.5"
                class="text-gray-medium hover:text-primary-beet"
                :aria-label="__('sort-ascending-label')"
                :disabled="isActiveSort('asc')"
                @click="$emit('sort', header.id, 'asc')"
            >
                <ArrowUpIcon class="size-3.5" />
            </IconButton>
            <IconButton
                v-if="header.sortable"
                key="desc"
                area-size="size-5"
                size="size-3.5"
                class="text-gray-medium hover:text-primary-beet"
                :aria-label="__('sort-descending-label')"
                :disabled="isActiveSort('desc')"
                @click="$emit('sort', header.id, 'desc')"
            >
                <ArrowDownIcon class="size-3.5" />
            </IconButton>

            <IconButton
                v-if="infoText"
                area-size="size-5"
                size="size-4"
                class="text-gray-medium hover:text-primary-beet"
                :aria-label="
                    __('info-column-aria-label', {
                        column: __untypedExpression(header.translationKey),
                    })
                "
                @click="infoModal = true"
            >
                <InfoIcon class="size-4" />
            </IconButton>
            <IconButton
                v-if="expandable"
                area-size="size-5"
                size="size-4"
                :aria-label="
                    __('expand-collapse-column', {
                        column: __untypedExpression(header.translationKey),
                    })
                "
                @click="$emit('toggleExpansion', header.id)"
            >
                <CircleMinusIcon v-if="expanded" class="size-4" />
                <CirclePlusIcon v-else class="size-4" />
            </IconButton>
        </div>
        <div
            v-if="!disableLayoutShift"
            class="hidden group-hover:flex absolute top-0 left-0 right-0 justify-between items-center px-1"
        >
            <IconButton area-size="size-5" size="size-4" aria-label="sort-asc">
                <MoveIcon class="size-4 cursor-move" />
            </IconButton>
            <IconButton
                class="text-red"
                area-size="size-5"
                size="size-4"
                :aria-label="
                    __('hide-column-aria-label', {
                        column: __untypedExpression(header.translationKey),
                    })
                "
                @click="onHideColumn"
            >
                <XIcon class="size-4" />
            </IconButton>
        </div>
    </div>
</template>
